import { Popover, Table, Tag } from 'antd';
import React from 'react';
import moment from 'moment';
import {
  DAY_FORMAT,
  DAYTIME_FORMAT,
  ORDER_STATUSES,
  ORDER_STATUSES_DISPLAY,
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY,
} from '../../constants';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const MOBILE_HIDDEN_KEYS = [
  'pickupAddress',
  'deliveryAddress',
  'pickupTimeWindow',
  'deliveryTimeWindow',
  'volume',
  'partner',
  'deliveryPhone',
  'orderType',
];

export default function OrdersTable({ withAction, dataSource, selectedRowKeys, onSelectChange, ...props }) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'orderCode',
      key: 'orderCode',
      fixed: 'left',
      width: 200,
      className: 'border-right',
    },
    {
      title: 'Route ID',
      dataIndex: 'routeId',
      key: 'routeId',
      width: 200,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'orderType',
      key: 'orderType',
      width: 140,
      render: (value) => {
        if (value === ORDER_TYPES.SERVICE) {
          return <Tag color="orange">{ORDER_TYPES_DISPLAY[value]}</Tag>;
        }
        return <Tag>{ORDER_TYPES_DISPLAY[value] || 'Standard'}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 250,
      render: (value) => {
        return value ? moment(value).format(DAYTIME_FORMAT) : '';
      },
    },
    {
      title: 'Delivery Date',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (value) => {
        return moment(value).format(DAY_FORMAT);
      },
    },
    {
      title: 'Pickup Address',
      dataIndex: 'pickupAddress',
      key: 'pickupAddress',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Delivery Address',
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Delivery Phone',
      dataIndex: 'deliveryPhone',
      key: 'deliveryPhone',
      width: 200,
    },
    {
      title: 'Pickup Time Window',
      dataIndex: 'pickupTimeWindow',
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.pickupTimeStart || '') + ' - ' + (record.pickupTimeEnd || '');
      },
    },
    {
      title: 'Delivery Time Window',
      dataIndex: 'deliveryTimeWindow',
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.deliveryTimeStart || '') + ' - ' + (record.deliveryTimeEnd || '');
      },
    },
    {
      title: 'Volume',
      dataIndex: 'volume',
      key: 'volume',
      width: 200,
    },
    {
      title: 'Partner',
      dataIndex: 'partner',
      key: 'partner',
      width: 200,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 250,
      render: (value) => {
        return value ? moment(value).format(DAYTIME_FORMAT) : '';
      },
    },
    {
      title: 'Scanned',
      dataIndex: 'scannedCode',
      key: 'scannedCode',
      width: 120,
      render: (value, record) => {
        if (!value) {
          return <span className="scan-status not-scanned">-</span>;
        }
        return (
          <Popover
            title="Scanned Code"
            content={
              <div className="scanned-code-popover">
                <div className="code">{value}</div>
                {record.scannedCodeType && <div className="type">Type: {record.scannedCodeType}</div>}
                {record.scannedAt && (
                  <div className="time">Scanned: {moment(record.scannedAt).format(DAYTIME_FORMAT)}</div>
                )}
              </div>
            }
          >
            <span className="scan-status scanned">Scanned</span>
          </Popover>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      width: 200,
      className: 'border-left',
      render: (value, record) => {
        return value === ORDER_STATUSES.ERROR && record.errorMessages?.length > 0 ? (
          <Popover
            title="Error Details"
            content={
              <div className="popupErrorMessages">
                {record.errorMessages.map((error, index) => {
                  return (
                    <div key={index} className="item">
                      {error}
                    </div>
                  );
                })}
              </div>
            }
          >
            <span className={'status order ' + value.toLowerCase()}>{ORDER_STATUSES_DISPLAY[value]}</span>
          </Popover>
        ) : (
          <span className={'status order ' + value.toLowerCase()}>{ORDER_STATUSES_DISPLAY[value]}</span>
        );
      },
    },
  ];

  if (withAction) {
    columns.push({
      key: 'action',
      width: 120,
      fixed: 'right',
      render(record) {
        return withAction(record);
      },
    });
  }

  const visibleColumns = isMobile
    ? columns.filter((col) => !MOBILE_HIDDEN_KEYS.includes(col.key || col.dataIndex))
    : columns;

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-orders table-common">
      <Table
        columns={visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // bordered
        {...props}
      />
    </div>
  );
}
